import React, { useState } from 'react';
import { Modal, Button, Form, Table, Row, Col } from 'react-bootstrap';
import { fmt } from '../../../../../helper/storeQuotation';
import moment from 'moment';

const AddRatesModal = ({ quotation, partyId, onSave, onClose, viewOnly = false }) => {

  const party = quotation.parties.find(p => p?.party_id?._id === partyId);

  const initRates = quotation.items.map(item => {
    const existing = item.party_rates?.find(r => r.party_id === partyId);
    return {
      item_main_id: item._id,
      size: item.size,
      item_name: item.item_name,
      quantity: item.quantity,
      rate: existing?.rate || "",
      discount: existing?.discount || 0,
      gst_percent: existing?.gst_percent || 18,
      make: existing?.make || "",
      remark: existing?.remark || ""
    };
  });

  const [rates, setRates] = useState(initRates);
  const [terms, setTerms] = useState({
    loading_charge: party?.loading_charge || "",
    delivery_terms: party?.delivery_terms || "At Workshop",
    payment_terms: party?.payment_terms || "30 Days",
    delivery_date: party?.delivery_date ? moment(party?.delivery_date).format("YYYY-MM-DD") : "",
    remark: party?.remark || ""
  });

  const updateRate = (idx, field, val) => {
    if (['rate', 'discount', 'gst_percent'].includes(field) && val < 0) return;
    setRates(r => r.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };

  const calcAmount = (rate, qty) => (parseFloat(rate) || 0) * (parseFloat(qty) || 0);
  const calcNet = (r) => {
    const amt = calcAmount(r.rate, r.quantity);
    const taxable = amt * (1 - (r.discount / 100));
    return taxable + (taxable * (r.gst_percent / 100));
  };
  const calcGSTAmount = (r) => {
    const amt = calcAmount(r.rate, r.quantity);
    const taxable = amt * (1 - (r.discount / 100));
    return taxable * (r.gst_percent / 100);
  };

  const totals = rates.reduce(
    (acc, r) => {
      const amount = calcAmount(r.rate, r.quantity);
      const taxable = amount * (1 - (r.discount / 100));
      const gstAmount = taxable * (r.gst_percent / 100);
      const net = taxable + gstAmount;

      acc.qty += parseFloat(r.quantity) || 0;
      acc.amount += amount;
      acc.gstAmount += gstAmount;
      acc.net += net;

      return acc;
    },
    { qty: 0, amount: 0, gstAmount: 0, net: 0 }
  );

  const hasValidRate = rates.some(r => r.rate !== "" && parseFloat(r.rate) >= 0);
  const hasInvalidItem = rates.some(r =>
    (r.rate !== "" && parseFloat(r.rate) < 0) ||
    parseFloat(r.discount) < 0 ||
    parseFloat(r.gst_percent) < 0
  );

  const isInvalid = !hasValidRate || hasInvalidItem || parseFloat(terms.loading_charge) < 0;

  return (
    <Modal show={true} onHide={onClose} size="xl" centered backdrop="static" dialogClassName="custom-modal-xl" >
      <Modal.Header closeButton>
        <div className="d-flex flex-column">
          <Modal.Title className='mb-0'>
            {viewOnly ? "View Rates:" : "Enter Rates:"} <span className="text-primary">{party?.party_snapshot?.name}</span>
          </Modal.Title>
          <div className="text-muted" style={{ fontSize: '12px', fontWeight: '500', marginTop: '2px' }}>
            {viewOnly ? "Step 3 — View prices received from this party" : "Step 3 — Enter prices received from this party"}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body style={{
        maxHeight: rates.length > 5 ? '70vh' : 'auto',
        overflowY: rates.length > 5 ? 'auto' : 'visible'
      }}>
        <div className="table-responsive" style={{ minHeight: "100px" }}>
          <Table bordered hover size="sm" style={{ minWidth: "1200px" }}>
            <thead className="bg-light small fw-bold">
              <tr>
                <th className='px-2' style={{ width: "160px" }}>Item</th>
                <th className='text-center px-2' style={{ width: '140px' }}>Size</th>
                <th className='text-center px-2' style={{ width: '50px' }}>Qty</th>
                <th className='text-center px-2' style={{ width: '100px' }}>Rate (₹) <span className="text-danger">*</span></th>
                <th className='text-center px-2' style={{ width: '70px' }}>Disc %</th>
                <th className='text-center px-2' style={{ width: '70px' }}>GST %</th>
                <th className='text-center px-2' style={{ width: '120px' }}>Amount (₹)</th>
                <th className='text-center px-2 text-nowrap' style={{ width: '100px' }}>GST Amount (₹)</th>
                <th className='text-end px-2 text-nowrap' style={{ width: '150px' }}>Net Amount (₹)</th>
                <th className='px-2' style={{ width: '120px' }}>Make</th>
                <th className='px-2' style={{ width: '120px' }}>Remark</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r, idx) => (
                <tr key={r.item_main_id}>
                  <td className="small fw-semibold align-content-center px-2 text-nowrap">{r.item_name}</td>
                  <td className="text-center align-content-center px-2">{r.size || "-"}</td>
                  <td className="text-center small fw-normal align-content-center px-2">{fmt(r.quantity)}</td>
                  <td className="px-2">
                    <Form.Control size="sm" type="number" readOnly={viewOnly} value={r.rate} onChange={e => updateRate(idx, "rate", e.target.value)} placeholder="0.00" />
                  </td>
                  <td className="px-2">
                    <Form.Control size="sm" type="number" readOnly={viewOnly} value={r.discount} onChange={e => updateRate(idx, "discount", e.target.value)} />
                  </td>
                  <td className="px-2">
                    <Form.Control size="sm" type="number" readOnly={viewOnly} value={r.gst_percent} onChange={e => updateRate(idx, "gst_percent", e.target.value)} />
                  </td>
                  <td className="text-end fw-bold small align-content-center px-2" style={{ color: "var(--primary-clr)" }}>
                    {r.rate ? fmt(calcAmount(r.rate, r.quantity)) : "-"}
                  </td>
                  <td className="text-end fw-bold small align-content-center px-2" style={{ color: "var(--primary-clr)" }}>
                    {r.rate ? fmt(calcGSTAmount(r)) : "-"}
                  </td>
                  <td className="text-end fw-bold text-success small align-content-center px-2">
                    {r.rate ? fmt(calcNet(r)) : "-"}
                  </td>
                  <td className="px-2">
                    <Form.Control size="sm" readOnly={viewOnly} value={r.make} onChange={e => updateRate(idx, "make", e.target.value)} placeholder="Make" />
                  </td>
                  <td className="px-2">
                    <Form.Control size="sm" readOnly={viewOnly} value={r.remark} onChange={e => updateRate(idx, "remark", e.target.value)} placeholder="Note" />
                  </td>
                </tr>
              ))}
              <tr className="bg-light fw-semibold">
                <td className="px-2 small fw-semibold">Total</td>

                <td></td>

                <td className="text-center small fw-semibold px-2">
                  {fmt(totals.qty)}
                </td>

                <td></td>

                <td></td>
                <td></td>

                <td className="text-end small fw-semibold px-2" >
                  {fmt(totals.amount)}
                </td>

                <td className="text-end small fw-semibold px-2">
                  {fmt(totals.gstAmount)}
                </td>

                <td className="text-end small fw-semibold px-2">
                  {fmt(totals.net)}
                </td>

                <td></td>
                <td></td>
              </tr>
            </tbody>
          </Table>
        </div>

        <Row className="mt-4">
          <Col md={3} className='mb-3'>
            <Form.Group>
              <Form.Label className="small fw-bold">Loading Charge (₹)</Form.Label>
              <Form.Control size="sm" type="number" min="0" placeholder="0.00" readOnly={viewOnly} value={terms.loading_charge} onChange={e => {
                if (e.target.value < 0) return;
                setTerms({ ...terms, loading_charge: e.target.value });
              }} />
            </Form.Group>
          </Col>
          <Col md={3} className='mb-3'>
            <Form.Group>
              <Form.Label className="small fw-bold">Delivery Terms</Form.Label>
              <Form.Control size="sm" readOnly={viewOnly} value={terms.delivery_terms} onChange={e => setTerms({ ...terms, delivery_terms: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={3} className='mb-3'>
            <Form.Group>
              <Form.Label className="small fw-bold">Payment Terms</Form.Label>
              <Form.Select size="sm" disabled={viewOnly} value={terms.payment_terms} onChange={e => setTerms({ ...terms, payment_terms: e.target.value })}>
                {["7 Days", "15 Days", "30 Days", "45 Days", "60 Days"].map(x => <option key={x} value={x}>{x}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3} className='mb-3'>
            <Form.Group>
              <Form.Label className="small fw-bold">Delivery Date</Form.Label>
              <Form.Control size="sm" type="date" readOnly={viewOnly} value={terms.delivery_date} onChange={e => setTerms({ ...terms, delivery_date: e.target.value })} />
            </Form.Group>
          </Col>
          <Col md={3} className='mb-3'>
            <Form.Group>
              <Form.Label className="small fw-bold">Vendor Remark</Form.Label>
              <Form.Control size="sm" readOnly={viewOnly} value={terms.remark} onChange={e => setTerms({ ...terms, remark: e.target.value })} placeholder="Vendor Remarks" />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <button className='btn btn-secondary btn-sm rounded-2 px-4 py-2' onClick={onClose}>Close</button>
        {!viewOnly && (
          <button className='btn btn-primary btn-sm rounded-2 px-4 py-2' disabled={isInvalid} onClick={() => onSave(partyId, rates, terms)}>Save Rates</button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AddRatesModal;
